"use client";
import {
  AlertTriangle,
  Archive,
  Binary,
  BookOpenText,
  Braces,
  Check,
  ChevronDown,
  CircleStop,
  Database,
  Download,
  File,
  FileArchive,
  FileCode2,
  FileDown,
  Fingerprint,
  FolderOpen,
  FolderTree,
  Gauge,
  GitFork,
  Hash,
  History,
  Layers3,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  Menu,
  Moon,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Play,
  Radar,
  RefreshCw,
  ScanLine,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Sun,
  Terminal,
  TextQuote,
  Upload,
  WandSparkles,
  Wrench,
  X,
} from "lucide-react";
import JSZip from "jszip";
import {
  type ChangeEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  analyzeJar,
} from "../lib/analyzer";
import {
  activateThreatIntelPack,
  importThreatIntelPack,
  type ImportedIntelPack,
} from "../lib/intel-pack";
import {
  clearInstalledThreatIntel,
  getThreatIntelCounts,
} from "../lib/threat-intel";
import {
  reportBundleReadme,
} from "../lib/report-export";
import {
  type Finding,
  type HistoryItem,
  type ScanProgress,
  type ScanSession,
  type Severity,
} from "../lib/types";
import {
  formatBytes,
} from "./shared/format";
import {
  downloadBlob,
} from "./shared/io";
import {
  AppLogo,
  IconButton,
  StatusDot,
} from "./shared/primitives";
import {
  type BatchScanItem,
  type WorkspaceView,
} from "./shared/types";
import {
  useToast,
} from "./shared/useToast";
import {
  verdictTone,
} from "./shared/verdict";
import {
  ActionsView,
} from "./views/analysis/ActionsView";
import {
  ClassesView,
} from "./views/analysis/ClassesView";
import {
  CodeQualityView,
} from "./views/analysis/CodeQualityView";
import {
  CoverageView,
} from "./views/analysis/CoverageView";
import {
  DataFlowView,
} from "./views/analysis/DataFlowView";
import {
  FindingsView,
} from "./views/analysis/FindingsView";
import {
  GraphView,
} from "./views/analysis/GraphView";
import {
  IndicatorsView,
} from "./views/analysis/IndicatorsView";
import {
  PayloadsView,
} from "./views/analysis/PayloadsView";
import {
  StringsView,
} from "./views/analysis/StringsView";
import {
  SummaryView,
} from "./views/analysis/SummaryView";
import {
  ExplorerView,
} from "./views/explorer/ExplorerView";
import {
  RepairRebuildView,
} from "./views/explorer/RepairRebuildView";
import {
  annotatedJavaSource,
  decompileClassWithRecoveredConstants,
} from "./views/explorer/panels";
import {
  CommunityDownloadsView,
} from "./views/external/CommunityDownloadsView";
import {
  ExternalAnalysisView,
} from "./views/external/ExternalAnalysisView";
import {
  IntelligenceRail,
} from "./views/intel/IntelligenceRail";
import {
  ThreatIntelView,
} from "./views/intel/ThreatIntelView";
import {
  YaraRulesView,
} from "./views/intel/YaraRulesView";
import {
  BatchQueueView,
} from "./views/reporting/BatchQueueView";
import {
  CompareView,
} from "./views/reporting/CompareView";
import {
  HistoryView,
} from "./views/reporting/HistoryView";
import {
  IntegrityView,
} from "./views/reporting/IntegrityView";
import {
  ManifestView,
} from "./views/reporting/ManifestView";
import {
  ReportsView,
} from "./views/reporting/ReportsView";


const HISTORY_KEY = "jarlab-scan-history-v1";

const OFFICIAL_LOOKUP_KEY = "jarlab-official-hash-lookup-v1";

const SIMPLE_MODE_KEY = "jarlab-simple-mode-v1";

const INTEL_PACK_KEY = "jarlab-intel-pack-v1";

const INTEL_PUBLIC_KEY = "jarlab-intel-public-key-v1";

const MAX_HISTORY = 100;


const navItems: Array<{
  id: WorkspaceView;
  label: string;
  icon: typeof LayoutDashboard;
}> = [
  { id: "summary", label: "Summary", icon: LayoutDashboard },
  { id: "community", label: "Community", icon: FolderOpen },
  { id: "explorer", label: "Explorer", icon: FolderTree },
  { id: "repair", label: "Deobfuscate", icon: Wrench },
  { id: "reports", label: "Reports", icon: FileDown },
  { id: "batch", label: "Batch queue", icon: Layers3 },
  { id: "external", label: "Dynamic sandbox", icon: Play },
  { id: "findings", label: "Findings", icon: ShieldAlert },
  { id: "actions", label: "Observed actions", icon: ScanLine },
  { id: "flows", label: "Data flow", icon: Network },
  { id: "indicators", label: "Indicators", icon: Radar },
  { id: "payloads", label: "Embedded payloads", icon: Binary },
  { id: "classes", label: "Classes", icon: Braces },
  { id: "graph", label: "Reference graph", icon: GitFork },
  { id: "strings", label: "Strings", icon: TextQuote },
  { id: "manifest", label: "Manifest", icon: BookOpenText },
  { id: "integrity", label: "Integrity", icon: ShieldCheck },
  { id: "compare", label: "Compare JARs", icon: RefreshCw },
  { id: "intel", label: "Threat intel", icon: Database },
  { id: "yara", label: "YARA rules", icon: FileCode2 },
  { id: "benchmark", label: "Coverage", icon: Gauge },
  { id: "quality", label: "Code quality", icon: WandSparkles },
  { id: "history", label: "History", icon: History },
];


// TEMPORARY: hide Community tab from navigation until the catalog is ready again.
const TEMP_HIDE_COMMUNITY_TAB = true;


const visibleNavItems = navItems.filter(
  (item) => !(TEMP_HIDE_COMMUNITY_TAB && item.id === "community"),
);


const primaryNavIds = new Set<WorkspaceView>([
  "summary",
  ...(TEMP_HIDE_COMMUNITY_TAB ? [] : (["community"] as WorkspaceView[])),
  "explorer",
  "repair",
  "reports",
  "batch",
  "findings",
  "actions",
]);


function EmptyWorkspace({
  onPick,
  onDemo,
  onDrop,
  dragActive,
  setDragActive,
  officialLookup,
}: {
  onPick: () => void;
  onDemo: () => void;
  onDrop: (files: File[]) => void;
  dragActive: boolean;
  setDragActive: (active: boolean) => void;
  officialLookup: boolean;
}) {
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length) onDrop(files);
  };

  return (
    <main className="empty-workspace">
      <div className="empty-grid" aria-hidden="true" />
      <section className="empty-intro">
        <div className="eyebrow">
          <span className="eyebrow-line" />
          ADVANCED STATIC ANALYSIS
        </div>
        <h1>
          Open the file.
          <br />
          <span>See what it actually does.</span>
        </h1>
        <p>
          A private malware-analysis workbench for JARs, containers, bytecode,
          native payloads, scripts, databases, documents, and opaque binaries.
        </p>
      </section>

      <section
        className={`drop-zone ${dragActive ? "is-dragging" : ""}`}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setDragActive(false);
          }
        }}
        onDrop={handleDrop}
        aria-label="Drop a file or archive to scan"
      >

        <div className="drop-icon">
          <Archive size={32} strokeWidth={1.5} />
          <ScanLine size={18} />
        </div>
        <div>
          <h2>{dragActive ? "Release to analyze" : "Drop files here"}</h2>
          <p>
            JAR, ZIP, APK, CLASS, BIN, native, script, or document · 50 MB each
            · up to 20 files
          </p>
        </div>
        <div className="drop-actions">
          <button className="primary-button" type="button" onClick={onPick}>
            <Upload size={16} />
            Choose files
          </button>
          <button className="secondary-button" type="button" onClick={onDemo}>
            <Play size={15} />
            Try diagnostic demo
          </button>
        </div>
        <div className="local-proof">
          <LockKeyhole size={13} />
          {officialLookup
            ? "File bytes stay local. Eligible JAR hashes can be checked against Modrinth."
            : "Analysis stays in this browser. The sample and hashes remain local."}
        </div>
      </section>

      <section className="capability-strip" aria-label="Analysis capabilities">
        <div>
          <Fingerprint size={17} />
          <span>Hash &amp; integrity</span>
        </div>
        <div>
          <Braces size={17} />
          <span>Class intelligence</span>
        </div>
        <div>
          <Radar size={17} />
          <span>IOC extraction</span>
        </div>
        <div>
          <ShieldAlert size={17} />
          <span>Data-flow tracing</span>
        </div>
      </section>

      <section className="empty-workbench-preview" aria-label="JarLab workspaces">
        <article>
          <span className="empty-preview-icon">
            <GitFork size={18} />
          </span>
          <div>
            <strong>Reachability-first detection</strong>
            <p>
              Suspicious code affects the verdict only when JarLab can connect
              it to a supported entry point or prove an active data-flow path.
            </p>
          </div>
          <code>ENTRY → SOURCE → SINK</code>
        </article>
        <article>
          <span className="empty-preview-icon">
            <WandSparkles size={18} />
          </span>
          <div>
            <strong>Three-stage deobfuscation</strong>
            <p>
              Recoverator can run through the paired local companion, with CFR,
              Vineflower, and verifier-safe browser rewriting as fallbacks.
            </p>
          </div>
          <code>RECOVER → VERIFY → EXPORT</code>
        </article>
        <article>
          <span className="empty-preview-icon">
            <Terminal size={18} />
          </span>
          <div>
            <strong>Optional local sandbox</strong>
            <p>
              One-click dynamic runs stay on this computer inside a disposable,
              network-disabled container. No cloud API key is required.
            </p>
          </div>
          <code>LOCALHOST · NO NETWORK</code>
        </article>
      </section>
    </main>
  );
}


function ScanOverlay({
  fileName,
  progress,
  onCancel,
}: {
  fileName: string;
  progress: ScanProgress;
  onCancel: () => void;
}) {
  const phases: ScanProgress["phase"][] = [
    "Reading file",
    "Unpacking container",
    "Hashing sample",
    "Indexing entries",
    "Parsing classes",
    "Tracing data flow",
    "Inspecting payloads",
    "Checking integrity",
    "Checking reputation",
    "Running detections",
    "Building report",
  ];
  const phaseIndex = phases.indexOf(progress.phase);
  return (
    <div className="scan-overlay" role="dialog" aria-modal="true">
      <div className="scan-panel">
        <div className="scan-emblem">
          <div className="scan-sweep" />
          <FileArchive size={36} strokeWidth={1.3} />
        </div>
        <div className="scan-kicker">LOCAL ANALYSIS IN PROGRESS</div>
        <h2>{fileName}</h2>
        <p>{progress.detail}</p>
        <div className="progress-track">
          <span style={{ width: `${progress.percent}%` }} />
        </div>
        <div className="progress-meta">
          <span>{progress.phase}</span>
          <strong>{progress.percent}%</strong>
        </div>
        <div className="phase-rail">
          {phases.map((phase, index) => (
            <div
              key={phase}
              className={
                index < phaseIndex
                  ? "is-complete"
                  : index === phaseIndex
                    ? "is-current"
                    : ""
              }
            >
              <span>
                {index < phaseIndex ? (
                  <Check size={11} />
                ) : index === phaseIndex ? (
                  <LoaderCircle size={11} />
                ) : null}
              </span>
              {phase}
            </div>
          ))}
        </div>
        <button className="quiet-button" type="button" onClick={onCancel}>
          <CircleStop size={15} />
          Cancel scan
        </button>
      </div>
    </div>
  );
}


function SettingsPanel({
  theme,
  setTheme,
  revealSecrets,
  setRevealSecrets,
  officialLookup,
  setOfficialLookup,
  simpleMode,
  setSimpleMode,
  historyCount,
  clearHistory,
  onClose,
}: {
  theme: "dark" | "light";
  setTheme: (theme: "dark" | "light") => void;
  revealSecrets: boolean;
  setRevealSecrets: (value: boolean) => void;
  officialLookup: boolean;
  setOfficialLookup: (value: boolean) => void;
  simpleMode: boolean;
  setSimpleMode: (value: boolean) => void;
  historyCount: number;
  clearHistory: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <button className="settings-scrim" type="button" aria-label="Close settings" onClick={onClose} />
      <aside className="settings-panel" role="dialog" aria-label="Workspace settings">
        <div className="settings-header">
          <div>
            <span>WORKSPACE</span>
            <h2>Settings</h2>
          </div>
          <IconButton label="Close settings" onClick={onClose}>
            <X size={17} />
          </IconButton>
        </div>
        <section>
          <h3>Appearance</h3>
          <div className="theme-choice">
            <button
              className={theme === "dark" ? "is-active" : ""}
              type="button"
              onClick={() => setTheme("dark")}
            >
              <Moon size={17} />
              Dark
            </button>
            <button
              className={theme === "light" ? "is-active" : ""}
              type="button"
              onClick={() => setTheme("light")}
            >
              <Sun size={17} />
              Light
            </button>
          </div>
          <label className="setting-toggle">
            <span>
              <strong>Simple workspace</strong>
              <small>
                Keep the core scan, explorer, deobfuscation, reports, and batch
                tools visible. Analyst tools remain under Advanced.
              </small>
            </span>
            <input
              type="checkbox"
              checked={simpleMode}
              onChange={(event) => setSimpleMode(event.target.checked)}
            />
            <i />
          </label>
        </section>
        <section>
          <h3>Sensitive evidence</h3>
          <label className="setting-toggle">
            <span>
              <strong>Reveal embedded secrets</strong>
              <small>
                Show full webhook and bot credentials in indicator tables.
              </small>
            </span>
            <input
              type="checkbox"
              checked={revealSecrets}
              onChange={(event) => setRevealSecrets(event.target.checked)}
            />
            <i />
          </label>
        </section>
        <section>
          <h3>Official provenance</h3>
          <label className="setting-toggle">
            <span>
              <strong>Modrinth hash lookup</strong>
              <small>
                Send only the analyzed SHA-512 to identify exact official
                releases. JAR bytes never leave this browser.
              </small>
            </span>
            <input
              type="checkbox"
              checked={officialLookup}
              onChange={(event) =>
                setOfficialLookup(event.target.checked)
              }
            />
            <i />
          </label>
        </section>
        <section>
          <h3>Local data</h3>
          <div className="storage-card">
            <span>
              <Database size={16} />
              <span>
                <strong>{historyCount} scan summaries</strong>
                <small>Stored in this browser</small>
              </span>
            </span>
            <button type="button" onClick={clearHistory} disabled={!historyCount}>
              Clear
            </button>
          </div>
        </section>
        <section className="engine-profile">
          <h3>Analysis profile</h3>
          <div>
            <ShieldCheck size={17} />
            <span>
              <strong>Bounded static analysis</strong>
              <small>
                50 MB upload · 300 MB primary extraction · bounded recovery ·
                no execution
              </small>
            </span>
          </div>
        </section>
      </aside>
    </>
  );
}


export default function Home() {
  const inputRef = useRef<HTMLInputElement>(null);
  const comparisonInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [session, setSession] = useState<ScanSession | null>(null);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [batchItems, setBatchItems] = useState<BatchScanItem[]>([]);
  const [comparisonSession, setComparisonSession] =
    useState<ScanSession | null>(null);
  const [activeView, setActiveView] = useState<WorkspaceView>("summary");
  // "community" is not a reachable view while the tab is hidden, so correct it
  // during render rather than in an effect: the effect form committed one frame
  // of the hidden view before snapping back. This is React's documented
  // "adjusting state when a prop changes" pattern, and the only state on this
  // path that can be out of range without a user action.
  if (TEMP_HIDE_COMMUNITY_TAB && activeView === "community") {
    setActiveView("summary");
  }
  const [scanningFile, setScanningFile] = useState("");
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const saved = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");
      return Array.isArray(saved) ? saved.slice(0, MAX_HISTORY) : [];
    } catch {
      return [];
    }
  });
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("jarlab-theme") === "light" ? "light" : "dark";
  });
  const [revealSecrets, setRevealSecrets] = useState(false);
  const [officialLookup, setOfficialLookup] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(OFFICIAL_LOOKUP_KEY) !== "false";
  });
  const [simpleMode, setSimpleMode] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(SIMPLE_MODE_KEY) !== "false";
  });
  const [intelPack, setIntelPack] = useState<ImportedIntelPack | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sourceExporting, setSourceExporting] = useState(false);
  const { toast, show: showToast } = useToast();
  const report = session?.report ?? null;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("jarlab-theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(OFFICIAL_LOOKUP_KEY, String(officialLookup));
  }, [officialLookup]);

  useEffect(() => {
    localStorage.setItem(SIMPLE_MODE_KEY, String(simpleMode));
  }, [simpleMode]);

  useEffect(() => {
    const saved = localStorage.getItem(INTEL_PACK_KEY);
    if (!saved) return;
    const publicKey = localStorage.getItem(INTEL_PUBLIC_KEY) ?? "";
    void importThreatIntelPack(saved, publicKey)
      .then((imported) => {
        activateThreatIntelPack(imported);
        setIntelPack(imported);
      })
      .catch(() => {
        localStorage.removeItem(INTEL_PACK_KEY);
        localStorage.removeItem(INTEL_PUBLIC_KEY);
        clearInstalledThreatIntel();
      });
  }, []);

  const saveHistory = useCallback((next: HistoryItem[]) => {
    const limited = next.slice(0, MAX_HISTORY);
    setHistory(limited);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(limited));
  }, []);

  const recordHistory = useCallback((item: HistoryItem) => {
    setHistory((current) => {
      const next = [
        item,
        ...current.filter((saved) => saved.sha256 !== item.sha256),
      ].slice(0, MAX_HISTORY);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const scanFile = useCallback(
    async (file: File, options?: { keepView?: boolean }) => {
      setError("");
      setScanningFile(file.name);
      setCurrentFile(file);
      setSession(null);
      setComparisonSession(null);
      if (!options?.keepView) setActiveView("summary");
      const controller = new AbortController();
      abortRef.current = controller;
      setProgress({
        phase: "Reading file",
        percent: 1,
        detail: "Opening local file",
      });
      try {
        const nextSession = await analyzeJar(
          file,
          (nextProgress) => setProgress(nextProgress),
          controller.signal,
          { officialLookup },
        );
        setSession(nextSession);
        setSelectedFinding(nextSession.report.findings[0] ?? null);
        const initialPath =
          nextSession.report.entries.find(
            (entry) => entry.kind === "manifest" && !entry.directory,
          )?.path ??
          nextSession.report.classes[0]?.path ??
          nextSession.report.entries.find((entry) => !entry.directory)?.path ??
          null;
        setSelectedPath(initialPath);
        const item: HistoryItem = {
          id: nextSession.report.id,
          fileName: nextSession.report.fileName,
          sha256: nextSession.report.hashes.sha256,
          score: nextSession.report.score,
          verdict: nextSession.report.verdict,
          completedAt: nextSession.report.completedAt,
          findings: nextSession.report.findings.length,
          fileSize: nextSession.report.fileSize,
        };
        recordHistory(item);
        setBatchItems([
          {
            id: `${nextSession.report.id}-${file.lastModified}`,
            fileName: file.name,
            size: file.size,
            status: "complete",
            file,
            session: nextSession,
          },
        ]);
        showToast("Analysis complete");
      } catch (scanError) {
        if (
          !(scanError instanceof DOMException && scanError.name === "AbortError")
        ) {
          setError(
            scanError instanceof Error
              ? scanError.message
              : "JarLab could not analyze this file.",
          );
        }
      } finally {
        abortRef.current = null;
        setProgress(null);
        setScanningFile("");
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [officialLookup, recordHistory, showToast],
  );

  const scanFiles = useCallback(
    async (incoming: File[]) => {
      const files = incoming.slice(0, 20);
      if (!files.length) return;
      if (files.length === 1) {
        await scanFile(files[0]);
        return;
      }
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
      if (totalBytes > 250 * 1024 * 1024) {
        setError("Batch uploads are limited to 250 MB total.");
        return;
      }
      setError("");
      setSession(null);
      setComparisonSession(null);
      setCurrentFile(null);
      setActiveView("batch");
      const initialItems: BatchScanItem[] = files.map((file, index) => ({
        id: `${file.name}-${file.lastModified}-${index}`,
        fileName: file.name,
        size: file.size,
        status: "queued",
        file,
      }));
      setBatchItems(initialItems);
      const controller = new AbortController();
      abortRef.current = controller;
      let latestSession: ScanSession | null = null;
      let latestFile: File | null = null;
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const itemId = initialItems[index].id;
        setBatchItems((items) =>
          items.map((item) =>
            item.id === itemId ? { ...item, status: "analyzing" } : item,
          ),
        );
        setScanningFile(`${file.name} (${index + 1}/${files.length})`);
        setProgress({
          phase: "Reading file",
          percent: 1,
          detail: `Batch item ${index + 1} of ${files.length}`,
        });
        try {
          const nextSession = await analyzeJar(
            file,
            (nextProgress) => setProgress(nextProgress),
            controller.signal,
            { officialLookup },
          );
          latestSession = nextSession;
          latestFile = file;
          setBatchItems((items) =>
            items.map((item) =>
              item.id === itemId
                ? { ...item, status: "complete", session: nextSession }
                : item,
            ),
          );
          recordHistory({
            id: nextSession.report.id,
            fileName: nextSession.report.fileName,
            sha256: nextSession.report.hashes.sha256,
            score: nextSession.report.score,
            verdict: nextSession.report.verdict,
            completedAt: nextSession.report.completedAt,
            findings: nextSession.report.findings.length,
            fileSize: nextSession.report.fileSize,
          });
        } catch (scanError) {
          if (
            scanError instanceof DOMException &&
            scanError.name === "AbortError"
          ) {
            break;
          }
          const message =
            scanError instanceof Error
              ? scanError.message
              : "JarLab could not analyze this file.";
          setBatchItems((items) =>
            items.map((item) =>
              item.id === itemId
                ? { ...item, status: "failed", error: message }
                : item,
            ),
          );
        }
      }
      abortRef.current = null;
      setProgress(null);
      setScanningFile("");
      if (latestSession) {
        setSession(latestSession);
        setCurrentFile(latestFile);
        setSelectedFinding(latestSession.report.findings[0] ?? null);
        setSelectedPath(
          latestSession.report.classes[0]?.path ??
            latestSession.report.entries.find((entry) => !entry.directory)
              ?.path ??
            null,
        );
      }
      setActiveView("batch");
      if (inputRef.current) inputRef.current.value = "";
      showToast(
        `${files.length.toLocaleString()} batch items finished or accounted for`,
      );
    },
    [officialLookup, recordHistory, scanFile, showToast],
  );

  const scanComparisonFile = useCallback(
    async (file: File) => {
      setError("");
      setScanningFile(file.name);
      const controller = new AbortController();
      abortRef.current = controller;
      setProgress({
        phase: "Reading file",
        percent: 1,
        detail: "Opening comparison archive",
      });
      try {
        const nextSession = await analyzeJar(
          file,
          (nextProgress) => setProgress(nextProgress),
          controller.signal,
          { officialLookup },
        );
        setComparisonSession(nextSession);
        setActiveView("compare");
        showToast("Comparison JAR analyzed");
      } catch (scanError) {
        if (
          !(scanError instanceof DOMException && scanError.name === "AbortError")
        ) {
          setError(
            scanError instanceof Error
              ? scanError.message
              : "JarLab could not analyze the comparison archive.",
          );
        }
      } finally {
        abortRef.current = null;
        setProgress(null);
        setScanningFile("");
        if (comparisonInputRef.current)
          comparisonInputRef.current.value = "";
      }
    },
    [officialLookup, showToast],
  );

  const installIntelPack = useCallback(
    async (raw: string, publicKey: string) => {
      const imported = await importThreatIntelPack(raw, publicKey);
      activateThreatIntelPack(imported);
      setIntelPack(imported);
      localStorage.setItem(INTEL_PACK_KEY, raw);
      localStorage.setItem(INTEL_PUBLIC_KEY, publicKey);
      showToast(
        imported.trusted && !imported.expired
          ? "Trusted intelligence pack installed"
          : "Unverified intelligence pack installed with reduced trust",
      );
    },
    [showToast],
  );

  const clearIntelPack = useCallback(() => {
    clearInstalledThreatIntel();
    setIntelPack(null);
    localStorage.removeItem(INTEL_PACK_KEY);
    localStorage.removeItem(INTEL_PUBLIC_KEY);
    showToast("External intelligence pack removed");
  }, [showToast]);

  const scanDiagnosticDemo = useCallback(async () => {
    setError("");
    try {
      const response = await fetch("/jarlab-diagnostic-sample.jar");
      if (!response.ok) throw new Error("Diagnostic sample is unavailable.");
      const bytes = await response.arrayBuffer();
      await scanFile(
        new globalThis.File([bytes], "diagnostic-sample.jar", {
          type: "application/java-archive",
        }),
      );
    } catch (demoError) {
      setError(
        demoError instanceof Error
          ? demoError.message
          : "JarLab could not open the diagnostic sample.",
      );
    }
  }, [scanFile]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "o") {
        event.preventDefault();
        inputRef.current?.click();
      }
      if (
        report &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "e"
      ) {
        event.preventDefault();
        downloadBlob(
          JSON.stringify(report, null, 2),
          `${report.id}-jarlab-report.json`,
          "application/json",
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [report]);

  const onFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length) void scanFiles(files);
  };

  const onComparisonFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void scanComparisonFile(file);
  };

  const copy = async (value: string, message = "Copied") => {
    try {
      await navigator.clipboard.writeText(value);
      showToast(message);
    } catch {
      showToast("Clipboard permission was denied");
    }
  };

  const exportReport = () => {
    if (!report) return;
    setActiveView("reports");
  };

  const exportReconstructedSource = async () => {
    if (!report || !session || sourceExporting) return;
    setSourceExporting(true);
    try {
      const archive = new JSZip();
      const maximumClasses = 1_000;
      const classes = report.classes.slice(0, maximumClasses);
      const failures: Array<{ className: string; error: string }> = [];
      for (let index = 0; index < classes.length; index += 1) {
        const classInfo = classes[index];
        try {
          const source = await decompileClassWithRecoveredConstants(
            report,
            session,
            classInfo,
          );
          const recovered = report.recoveredExpressions.filter(
            (item) =>
              item.entry === classInfo.path ||
              item.className === classInfo.name,
          );
          const flows = report.dataFlows.filter(
            (item) =>
              item.entry === classInfo.path ||
              item.steps.some((step) => step.className === classInfo.name),
          );
          const safePath = classInfo.name
            .split(".")
            .filter((part) => part && part !== "." && part !== "..")
            .join("/");
          archive.file(
            `src/${safePath || classInfo.simpleName}.java`,
            annotatedJavaSource(source.recovered, recovered, flows),
          );
        } catch (sourceError) {
          failures.push({
            className: classInfo.name,
            error:
              sourceError instanceof Error
                ? sourceError.message
                : "Decompiler failed",
          });
        }
        if (index % 10 === 9) {
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        }
      }
      archive.file(
        "jarlab/recovered-values.json",
        JSON.stringify(report.recoveredExpressions, null, 2),
      );
      archive.file(
        "jarlab/data-flows.json",
        JSON.stringify(report.dataFlows, null, 2),
      );
      archive.file(
        "jarlab/observed-actions.json",
        JSON.stringify(report.actions, null, 2),
      );
      archive.file(
        "jarlab/decompilation-failures.json",
        JSON.stringify(failures, null, 2),
      );
      archive.file("jarlab/MANIFEST.MF", report.manifestRaw || "");      archive.file(
        "README.md",
        reportBundleReadme(report, {
          decompiledClasses: classes.length - failures.length,
          decompilerFailures: failures.length,
          materializationLimit: maximumClasses,
        }),
      );
      const output = await archive.generateAsync({
        type: "uint8array",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      downloadBlob(
        new Uint8Array(output),
        `${report.id}-deobfuscated-source.zip`,
        "application/zip",
      );
      showToast("Deobfuscated source bundle downloaded");
    } catch (sourceError) {
      setError(
        sourceError instanceof Error
          ? sourceError.message
          : "JarLab could not build the source bundle.",
      );
    } finally {
      setSourceExporting(false);
    }
  };

  const resetWorkspace = () => {
    setSession(null);
    setCurrentFile(null);
    setBatchItems([]);
    setComparisonSession(null);
    setError("");
    setActiveView("summary");
    setSelectedFinding(null);
    setSelectedPath(null);
    setMobileNavOpen(false);
  };

  const clearHistory = () => {
    saveHistory([]);
    showToast("Local history cleared");
  };

  const removeHistory = (id: string) => {
    saveHistory(history.filter((item) => item.id !== id));
  };

  const openFinding = (finding: Finding) => {
    setSelectedFinding(finding);
    setActiveView("findings");
  };

  const navigate = (view: WorkspaceView) => {
    setActiveView(view);
    setMobileNavOpen(false);
  };

  const openBatchItem = (item: BatchScanItem) => {
    if (!item.session) return;
    setSession(item.session);
    setCurrentFile(item.file ?? null);
    setSelectedFinding(item.session.report.findings[0] ?? null);
    setSelectedPath(
      item.session.report.classes[0]?.path ??
        item.session.report.entries.find((entry) => !entry.directory)?.path ??
        null,
    );
    setActiveView("summary");
  };

  const titleByView: Record<WorkspaceView, string> = {
    summary: "Analysis summary",
    community: "Community downloads",
    actions: "Observed actions",
    flows: "Source-to-sink data flow",
    findings: "Detection findings",
    indicators: "Extracted indicators",
    payloads: "Embedded payload analysis",
    explorer: "Archive explorer",
    repair: "Deobfuscate & rebuild",
    reports: "Export reports",
    batch: "Bulk analysis queue",
    external: "Dynamic and external analysis",
    quality: "AI-style code signals",
    classes: "Class intelligence",
    graph: "Class reference graph",
    strings: "String index",
    manifest: "Archive manifest",
    integrity: "Integrity & provenance",
    compare: "JAR version comparison",
    intel: "Threat intelligence",
    yara: "YARA rules",
    benchmark: "Coverage & benchmarks",
    history: "Local scan history",
  };

  const navCount = (id: WorkspaceView) => {
    if (id === "actions") return report?.actions.length;
    if (id === "batch") return batchItems.length;
    if (id === "reports") return 4;
    if (id === "quality") return report?.codeQuality.signals.length;
    if (id === "flows") return report?.dataFlows.length;
    if (id === "findings") return report?.findings.length;
    if (id === "indicators") return report?.indicators.length;
    if (id === "payloads") return report?.embeddedPayloads.length;
    if (id === "classes") return report?.classes.length;
    if (id === "graph") return report?.entryPoints.length;
    if (id === "history") return history.length;
    if (id === "intel") return getThreatIntelCounts().total;
    return undefined;
  };

  const renderNavItem = (item: (typeof navItems)[number]) => {
    const Icon = item.icon;
    const count = navCount(item.id);
    return (
      <button
        key={item.id}
        className={activeView === item.id ? "is-active" : ""}
        type="button"
        onClick={() => navigate(item.id)}
        title={!sidebarOpen ? item.label : undefined}
      >
        <Icon size={17} />
        {sidebarOpen && <span>{item.label}</span>}
        {sidebarOpen && count !== undefined && <small>{count}</small>}
      </button>
    );
  };

  return (
    <div className="app-shell">
      <input
        ref={inputRef}
        className="file-input"
        type="file"
        multiple
        accept=".jar,.zip,.apk,.class,.bin,.dex,.wasm,.tar,.gz,.tgz,.pdf,.sqlite,.db,.exe,.dll,.so,.dylib,.ps1,.py,.js,.sh,.bat,.cmd,.mcpack,.mrpack,application/java-archive,application/zip,application/octet-stream"
        onChange={onFileInput}
      />
      <input
        ref={comparisonInputRef}
        className="file-input"
        type="file"
        accept=".jar,.zip,.mcpack,.mrpack,application/java-archive,application/zip"
        onChange={onComparisonFileInput}
      />
      <header className="topbar">
        <div className="topbar-left">
          {report && (
            <IconButton
              label="Toggle navigation"
              className="mobile-menu-button"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <Menu size={18} />
            </IconButton>
          )}
          <AppLogo />
          <span className="edition-badge">
            {simpleMode ? "SIMPLE" : "ANALYST"}
          </span>
        </div>
        <div className="topbar-status">
          <span className="privacy-state">
            <span />
            {officialLookup ? "LOCAL BYTES · HASH LOOKUP" : "FULLY LOCAL"}
          </span>
          <span className="shortcut-hint">
            <kbd>⌘</kbd>
            <kbd>O</kbd>
            new scan
          </span>
        </div>
        <div className="topbar-actions">
          {!TEMP_HIDE_COMMUNITY_TAB && (
            <button
              className={`secondary-button desktop-tool community-button ${
                activeView === "community" ? "is-active" : ""
              }`}
              type="button"
              onClick={() =>
                setActiveView((current) =>
                  current === "community" ? "summary" : "community",
                )
              }
            >
              <FolderOpen size={14} />
              Community
            </button>
          )}
          {report && (
            <>
              <button
                className="secondary-button desktop-tool"
                type="button"
                disabled={sourceExporting || report.classes.length === 0}
                onClick={() => void exportReconstructedSource()}
                title={
                  report.classes.length
                    ? "Download decompiled source with recovered values and data-flow annotations"
                    : "No parsed classes are available"
                }
              >
                {sourceExporting ? (
                  <LoaderCircle size={14} />
                ) : (
                  <Sparkles size={14} />
                )}
                {sourceExporting ? "Rebuilding…" : "Source bundle"}
              </button>
              <button className="secondary-button desktop-tool" type="button" onClick={exportReport}>
                <Download size={14} />
                Export
              </button>
              <button className="secondary-button compact" type="button" onClick={() => inputRef.current?.click()}>
                <Upload size={14} />
                New scan
              </button>
              <button
                className="primary-button compact topbar-deobfuscate"
                type="button"
                onClick={() => navigate("repair")}
              >
                <Wrench size={14} />
                Deobfuscate JAR
              </button>
            </>
          )}
          <IconButton
            label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </IconButton>
          <IconButton
            label="Workspace settings"
            active={settingsOpen}
            onClick={() => setSettingsOpen(true)}
          >
            <Settings2 size={16} />
          </IconButton>
        </div>
      </header>

      {!report && activeView === "community" ? (
        <main className="community-standalone">
          <CommunityDownloadsView />
        </main>
      ) : !report ? (
        <EmptyWorkspace
          onPick={() => inputRef.current?.click()}
          onDemo={scanDiagnosticDemo}
          onDrop={scanFiles}
          dragActive={dragActive}
          setDragActive={setDragActive}
          officialLookup={officialLookup}
        />
      ) : (
        <div className={`workspace-shell ${simpleMode ? "is-simple" : ""}`}>
          <aside
            className={`workspace-sidebar ${sidebarOpen ? "" : "is-collapsed"} ${
              mobileNavOpen ? "is-mobile-open" : ""
            }`}
          >
            <div className="file-summary">
              <span className={`file-verdict tone-${verdictTone(report.verdict)}`}>
                <FileArchive size={20} />
              </span>
              {sidebarOpen && (
                <span>
                  <strong title={report.fileName}>{report.fileName}</strong>
                  <small>
                    {formatBytes(report.fileSize)} · {report.containerType}
                  </small>
                </span>
              )}
            </div>
            <nav aria-label="Workspace sections">
              {visibleNavItems.filter((item) => primaryNavIds.has(item.id)).map(renderNavItem)}
              {sidebarOpen && (
                <details className="sidebar-more">
                  <summary>
                    <span>Advanced tools</span>
                    <ChevronDown size={14} />
                  </summary>
                  <div>
                    {visibleNavItems
                      .filter((item) => !primaryNavIds.has(item.id))
                      .map(renderNavItem)}
                  </div>
                </details>
              )}
            </nav>
            <div className="sidebar-bottom">
              <button
                className="sidebar-reset"
                type="button"
                onClick={resetWorkspace}
                title={!sidebarOpen ? "Close workspace" : undefined}
              >
                <X size={16} />
                {sidebarOpen && <span>Close workspace</span>}
              </button>
              <button
                className="collapse-button"
                type="button"
                onClick={() => setSidebarOpen((open) => !open)}
                aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              >
                {sidebarOpen ? (
                  <PanelLeftClose size={16} />
                ) : (
                  <PanelLeftOpen size={16} />
                )}
              </button>
            </div>
          </aside>
          {mobileNavOpen && (
            <button
              className="mobile-nav-scrim"
              type="button"
              aria-label="Close navigation"
              onClick={() => setMobileNavOpen(false)}
            />
          )}

          <div className="workspace-center">
            <div className="workspace-context">
              <div>
                <span>WORKSPACE / {activeView.toUpperCase()}</span>
                <h1>{titleByView[activeView]}</h1>
              </div>
              <div className="context-meta">
                <span className={`verdict-chip tone-${verdictTone(report.verdict)}`}>
                  <StatusDot
                    severity={
                      verdictTone(report.verdict) as Severity | "safe"
                    }
                  />
                  {report.verdict}
                </span>
                <span className="scan-id">
                  <Hash size={12} />
                  {report.id}
                </span>
              </div>
            </div>
            <main
              className={`workspace-main ${
                ["findings", "explorer", "graph", "flows"].includes(activeView)
                  ? "is-flush"
                  : ""
              }`}
            >
              {activeView === "summary" && (
                <SummaryView
                  report={report}
                  onOpenFinding={openFinding}
                  onNavigate={navigate}
                />
              )}
              {activeView === "community" && <CommunityDownloadsView />}
              {activeView === "findings" && (
                <FindingsView
                  report={report}
                  selected={selectedFinding}
                  setSelected={setSelectedFinding}
                />
              )}
              {activeView === "actions" && <ActionsView report={report} />}
              {activeView === "flows" && (
                <DataFlowView
                  report={report}
                  onOpenEntry={(path) => {
                    setSelectedPath(path);
                    setActiveView("explorer");
                  }}
                />
              )}
              {activeView === "indicators" && (
                <IndicatorsView
                  report={report}
                  revealSecrets={revealSecrets}
                  onCopy={copy}
                />
              )}
              {activeView === "payloads" && (
                <PayloadsView
                  report={report}
                  onOpenEntry={(path) => {
                    setSelectedPath(path);
                    setActiveView("explorer");
                  }}
                  onCopy={copy}
                />
              )}
              {activeView === "explorer" && (
                <ExplorerView
                  report={report}
                  session={session!}
                  selectedPath={selectedPath}
                  setSelectedPath={setSelectedPath}
                  onCopy={copy}
                  onToast={showToast}
                />
              )}
              {activeView === "repair" && (
                <RepairRebuildView
                  key={report.id}
                  report={report}
                  session={session!}
                  file={currentFile}
                  onToast={showToast}
                />
              )}
              {activeView === "reports" && (
                <ReportsView report={report} onToast={showToast} />
              )}
              {activeView === "batch" && (
                <BatchQueueView
                  items={batchItems}
                  onOpen={openBatchItem}
                  onToast={showToast}
                />
              )}
              {activeView === "external" && (
                <ExternalAnalysisView
                  report={report}
                  file={currentFile}
                  onCopy={copy}
                />
              )}
              {activeView === "quality" && (
                <CodeQualityView report={report} />
              )}
              {activeView === "classes" && (
                <ClassesView
                  report={report}
                  onOpenClass={(item) => {
                    setSelectedPath(item.path);
                    setActiveView("explorer");
                  }}
                />
              )}
              {activeView === "graph" && <GraphView report={report} />}
              {activeView === "strings" && (
                <StringsView report={report} onCopy={copy} />
              )}
              {activeView === "manifest" && (
                <ManifestView report={report} onCopy={copy} />
              )}
              {activeView === "integrity" && (
                <IntegrityView report={report} onCopy={copy} />
              )}
              {activeView === "compare" && (
                <CompareView
                  report={report}
                  comparisonReport={comparisonSession?.report ?? null}
                  onPick={() => comparisonInputRef.current?.click()}
                  onClear={() => setComparisonSession(null)}
                />
              )}
              {activeView === "intel" && (
                <ThreatIntelView
                  installed={intelPack}
                  onInstall={installIntelPack}
                  onClear={clearIntelPack}
                />
              )}
              {activeView === "yara" && (
                <YaraRulesView
                  report={report}
                  onRescan={
                    currentFile
                      ? () => {
                          void scanFile(currentFile, { keepView: true });
                        }
                      : undefined
                  }
                />
              )}
              {activeView === "benchmark" && (
                <CoverageView report={report} />
              )}
              {activeView === "history" && (
                <HistoryView
                  history={history}
                  clearHistory={clearHistory}
                  removeHistory={removeHistory}
                />
              )}
            </main>
          </div>
          {!simpleMode && <IntelligenceRail report={report} onCopy={copy} />}
        </div>
      )}

      {progress && (
        <ScanOverlay
          fileName={scanningFile}
          progress={progress}
          onCancel={() => abortRef.current?.abort()}
        />
      )}
      {error && (
        <div className="error-toast" role="alert">
          <AlertTriangle size={17} />
          <span>{error}</span>
          <button type="button" onClick={() => setError("")} aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      {toast && (
        <div className="success-toast" role="status">
          <Check size={14} />
          {toast}
        </div>
      )}
      {settingsOpen && (
        <SettingsPanel
          theme={theme}
          setTheme={setTheme}
          revealSecrets={revealSecrets}
          setRevealSecrets={setRevealSecrets}
          officialLookup={officialLookup}
          setOfficialLookup={setOfficialLookup}
          simpleMode={simpleMode}
          setSimpleMode={setSimpleMode}
          historyCount={history.length}
          clearHistory={clearHistory}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}
